/**
 * The ONE axios instance and Orval mutator (03-02 §5).
 *
 * This is the single place axios is imported. Orval-generated hooks call
 * `customInstance` as their HTTP mutator; it injects the configured baseURL,
 * unwraps the `{ success, data, error }` envelope to `data` (mirroring the
 * `spec/unwrap-envelope.ts` transformer — non-envelope responses pass through
 * untouched), and rejects with a `NormalizedApiError`.
 *
 * The layer holds NO auth logic. `@smartsash/auth` injects behavior via
 * `registerHttpHooks`: an auth-header provider and a single-flight
 * `onUnauthorized` handler that owns refresh/redirect policy.
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { normalizeError } from './getErrorMessage';

const DEFAULT_TIMEOUT_MS = 30_000;
const STATUS_UNAUTHORIZED = 401;
const STATUS_FORBIDDEN = 403;

export interface ApiClientConfig {
  /** Base URL of sash-admin-api. Injected by the app — never read from import.meta.env here. */
  baseURL: string;
  /** Request timeout; defaults to 30s. */
  timeoutMs?: number;
  /** Send cookies with cross-site requests (cookie-based auth). Defaults to true. */
  withCredentials?: boolean;
  /**
   * Default headers sent with every request, e.g. the login channel (HS-1615). A per-request
   * header of the same name wins, and so does the `Authorization` header from the auth hook.
   */
  headers?: Record<string, string>;
}

export interface HttpHooks {
  /** Auth header value (e.g. `Bearer …`) or null in cookie mode. Registered by @smartsash/auth. */
  getAuthHeader(): Promise<string | null> | string | null;
  /** Invoked once per 401 burst; auth refreshes then calls `retry`. Owns redirect policy. */
  onUnauthorized(ctx: { retry: () => Promise<unknown> }): Promise<unknown>;
  /**
   * Observe a 403 (HS-1387). Unlike `onUnauthorized` this does NOT get to change the outcome:
   * the request still rejects with its normalized error, whatever the hook does.
   *
   * That asymmetry is the decision. A 401 has one honest recovery — refresh the credential and
   * retry — while a 403 usually means exactly what it says, and retrying it would double every
   * genuinely refused request, non-idempotent ones included. So the hook is a place to log
   * centrally and to re-read a permission snapshot that may be stale, not a place to retry.
   *
   * It must never throw: a rejection here would replace the real API error with the hook's own.
   */
  onForbidden(ctx: { status: number; url?: string }): void;
}

/**
 * Per-request opt-out from the auth-policy hooks.
 *
 * The refresh call itself must set `skipAuthRefresh`, otherwise a 401 from `/auth/refresh` would
 * recurse into another refresh (mirrors the original panel's `_skipAuthRefresh`).
 *
 * `skipForbiddenPolicy` is the same guard one status along (HS-1387): the 403 hook re-reads
 * `/Auth/me`, so a 403 on `/Auth/me` itself would re-read it again, and again. Single-flight
 * does not help — those calls are sequential, not concurrent — so the request that carries the
 * policy has to opt out of it.
 */
export interface RequestAuthOptions {
  skipAuthRefresh?: boolean;
  skipForbiddenPolicy?: boolean;
}

let instance: AxiosInstance | null = null;
let hooks: Partial<HttpHooks> = {};

/**
 * Serialise a repeated query parameter the way the backend reads it.
 *
 * Axios spells an array as `search[]=a&search[]=b`. ASP.NET Core binds `string[] search` from
 * `search=a&search=b` and does not recognise the bracketed form, so the value silently failed to
 * bind — and on the endpoints that validate their query string, `search[]` is an unknown parameter
 * and the whole request comes back `400 Bad Request`. Every multi-value filter the list screens
 * send (`institutionId`, `labId`, `groupId`, `role`, `city`, `level`, …) has the same shape, so
 * this belongs on the instance rather than at any one call site.
 *
 * `null` and `undefined` entries are dropped rather than sent as empty, which is how an optional
 * parameter is meant to be absent.
 */
function serializeParams(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  /**
   * A query parameter is a scalar or a `Date`; anything else has no query-string spelling and
   * would arrive as `[object Object]`, which is a bug worth dropping rather than sending.
   */
  const append = (key: string, value: unknown): void => {
    if (typeof value === 'string') search.append(key, value);
    else if (typeof value === 'number' || typeof value === 'boolean')
      search.append(key, String(value));
    else if (value instanceof Date) search.append(key, value.toISOString());
  };

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const entry of value) append(key, entry);
      continue;
    }
    append(key, value);
  }
  return search.toString();
}

/** Create (or replace) the axios instance. Call once at app bootstrap before any request. */
export function configureApiClient(config: ApiClientConfig): void {
  instance = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    withCredentials: config.withCredentials ?? true,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...config.headers },
    paramsSerializer: serializeParams,
  });
}

/** Register auth behavior. Merges over any previously registered hooks. */
export function registerHttpHooks(next: Partial<HttpHooks>): void {
  hooks = { ...hooks, ...next };
}

/** Test/teardown seam: drop the instance and hooks. */
export function resetApiClient(): void {
  instance = null;
  hooks = {};
}

function getInstance(): AxiosInstance {
  if (!instance) {
    throw new Error(
      '@smartsash/api-client: configureApiClient({ baseURL }) must run before any request.',
    );
  }
  return instance;
}

interface Envelope {
  success: boolean;
  data: unknown;
  error?: unknown;
}

/**
 * Runtime mirror of the type transformer: an object carrying both `success` and
 * `data` is the envelope. Raw DTOs, inline booleans and empty 204s lack this
 * shape and pass through untouched.
 */
function isEnvelope(body: unknown): body is Envelope {
  return typeof body === 'object' && body !== null && 'success' in body && 'data' in body;
}

/** Unwrap an enveloped response to `data`, or throw on `{ success: false }`. */
function unwrap(response: AxiosResponse): unknown {
  const body: unknown = response.data;
  if (isEnvelope(body)) {
    if (!body.success) {
      // The whole envelope, not just `error`: `normalizeError` reads the prose out of it either
      // way, and passing the envelope is what lets a caller reach `errorCode` and a typed `data`
      // payload on the failure path (HS-1288).
      throw normalizeError(new Error('API returned success=false'), {
        status: response.status,
        body,
      });
    }
    return body.data;
  }
  return body;
}

/**
 * The error body of a binary request, decoded (HS-1659). With `responseType: 'blob'` a refusal
 * arrives as bytes too — a Blob in the browser, text under Node's adapter — which hides the
 * `{ success, errorCode, error }` JSON inside; parse it back so `normalizeError` sees the same body
 * a JSON request would. Only binary requests go through here: a JSON request's text body stays text.
 */
async function decodeBinaryErrorBody(body: unknown): Promise<unknown> {
  const text =
    typeof Blob !== 'undefined' && body instanceof Blob
      ? await body.text().catch(() => '')
      : typeof body === 'string'
        ? body
        : undefined;
  if (text === undefined) return body;
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * Send one request under the shared policy: auth header, the single-flight 401 refresh, the 403
 * observer, normalized errors. `read` turns a successful response into the caller's value — the
 * envelope unwrap for `customInstance`, the bytes and file name for `downloadFile`.
 */
async function request<T>(
  config: AxiosRequestConfig,
  options: (AxiosRequestConfig & RequestAuthOptions) | undefined,
  read: (response: AxiosResponse) => T,
): Promise<T> {
  const client = getInstance();
  const skipAuthRefresh =
    (config as RequestAuthOptions).skipAuthRefresh === true ||
    (options as RequestAuthOptions | undefined)?.skipAuthRefresh === true;
  const skipForbiddenPolicy =
    (config as RequestAuthOptions).skipForbiddenPolicy === true ||
    (options as RequestAuthOptions | undefined)?.skipForbiddenPolicy === true;

  const send = async (): Promise<AxiosResponse> => {
    const authHeader = await hooks.getAuthHeader?.();
    const headers: Record<string, string> = {
      ...(config.headers as Record<string, string> | undefined),
      ...(options?.headers as Record<string, string> | undefined),
    };
    if (authHeader) headers.Authorization = authHeader;
    return client.request({ ...config, ...options, headers });
  };

  /** Turn a failure into the `NormalizedApiError` callers branch on, telling the 403 observer. */
  const fail = async (err: unknown): Promise<never> => {
    if (axios.isAxiosError(err)) {
      if (err.response?.status === STATUS_FORBIDDEN && hooks.onForbidden && !skipForbiddenPolicy) {
        // Observation only — the error below is thrown either way. A hook that threw would
        // replace the API's own failure with its own, so it is contained here rather than
        // trusted to behave.
        try {
          hooks.onForbidden({ status: STATUS_FORBIDDEN, url: config.url });
        } catch {
          // Nothing useful to do with a broken observer, and the real error is on its way.
        }
      }
      throw normalizeError(err, {
        status: err.response?.status,
        body:
          config.responseType === 'blob'
            ? await decodeBinaryErrorBody(err.response?.data)
            : err.response?.data,
        timeout: err.code === 'ECONNABORTED',
      });
    }
    throw normalizeError(err);
  };

  try {
    return read(await send());
  } catch (err) {
    if (
      axios.isAxiosError(err) &&
      err.response?.status === STATUS_UNAUTHORIZED &&
      hooks.onUnauthorized &&
      !skipAuthRefresh
    ) {
      // The retry after a refresh fails the same way as the first try: normalized, its binary
      // body decoded, so a caller still sees the server's status and `errorCode`.
      const retry = async () => {
        try {
          return read(await send());
        } catch (retryError) {
          return fail(retryError);
        }
      };
      return (await hooks.onUnauthorized({ retry })) as T;
    }
    return fail(err);
  }
}

/** The Orval mutator. Injects auth header, unwraps the envelope, normalizes errors. */
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig & RequestAuthOptions,
): Promise<T> => request(config, options, (response) => unwrap(response) as T);

/** A downloaded file: its bytes and the name the server gave it, when it gave one. */
export interface DownloadedFile {
  blob: Blob;
  /** From `Content-Disposition`; `null` when the header is absent or names no file. */
  fileName: string | null;
}

/**
 * The file name a `Content-Disposition` header carries. The RFC 6266 `filename*` (RFC 5987
 * encoded, `UTF-8''…`) wins over the plain `filename`, which a server may have reduced to ASCII.
 */
export function fileNameFromDisposition(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)?.[1];
  if (extended !== undefined) {
    try {
      const decoded = decodeURIComponent(extended.trim());
      if (decoded !== '') return decoded;
    } catch {
      // A malformed escape falls through to the plain parameter.
    }
  }
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header);
  const name = (plain?.[1] ?? plain?.[2])?.trim();
  return name === undefined || name === '' ? null : name;
}

/**
 * Download a file through the shared instance (HS-1659): the same auth, refresh and 403 policy as
 * every generated call, but the body stays binary and `Content-Disposition` is kept. A refusal
 * rejects with a `NormalizedApiError` carrying the server's `errorCode`, read out of the binary error body.
 */
export const downloadFile = (
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig & RequestAuthOptions,
): Promise<DownloadedFile> =>
  request({ ...config, responseType: 'blob' }, options, (response) => {
    // A browser answers a Blob; Node's adapter hands back the body as text instead.
    const body: unknown = response.data;
    const blob = body instanceof Blob ? body : new Blob([typeof body === 'string' ? body : '']);
    return {
      blob,
      fileName: fileNameFromDisposition(response.headers['content-disposition']),
    };
  });

export default customInstance;
